// DepositoPanel — captain (or Ops) uploads the client-deposit bank voucher for a
// closed event. Reads /events/:id/deposito-status, shows the state, and lets a
// pending/en-revisión event accept a photo/PDF → OCR + auto-reconcile via
// miniapp-api → sophi-bot. The expected factura amount IS shown here (the captain
// is the money handler) — this surface is private to captain/Ops, never a group.
//
// Reused by CerrarEvento's terminal card and the event detail modal so the captain
// DM's "Eventos → [EV] → Depósito" deep-link always lands on the same control.

import { useEffect, useState } from 'preact/hooks';
import { getDepositoStatus, uploadDepositoVoucher, ApiError, type DepositoStatus } from '../lib/api';
import { haptic } from '../lib/telegram';

interface DepositoPanelProps {
  eventId: string;
}

const BADGE: Record<string, { txt: string; bg: string; fg: string }> = {
  Conciliado: { txt: '✅ Depósito recibido', bg: 'rgba(52,199,89,0.14)', fg: 'var(--color-success)' },
  En_Revision: { txt: '🔎 En revisión', bg: 'rgba(255,149,0,0.14)', fg: 'var(--color-warning)' },
  Pendiente: { txt: '⏳ Falta comprobante', bg: 'rgba(255,149,0,0.14)', fg: 'var(--color-warning)' },
  Mensual: { txt: '📅 Mensual', bg: 'var(--color-secondary)', fg: 'var(--color-hint)' },
  No_Aplica: { txt: '—', bg: 'var(--color-secondary)', fg: 'var(--color-hint)' },
};

function fmt(n?: number | null): string {
  return n != null ? n.toLocaleString('es-DO') : '';
}

export function DepositoPanel({ eventId }: DepositoPanelProps) {
  const [status, setStatus] = useState<DepositoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metodo, setMetodo] = useState('Transferencia_Cliente');
  const [file, setFile] = useState<File | null>(null);
  // Non-captain/non-Ops viewers get 403 from the gated endpoint — hide silently
  // so the panel is safe to drop into the shared event detail for everyone.
  const [hidden, setHidden] = useState(false);

  async function refresh() {
    try {
      setStatus(await getDepositoStatus(eventId));
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) { setHidden(true); }
      else setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, [eventId]);

  if (hidden) return null;

  async function onSubmit() {
    if (!file) { setError('Selecciona o pega una foto del comprobante.'); return; }
    setBusy(true);
    setError(null);
    haptic('warning');
    try {
      const r = await uploadDepositoVoucher(eventId, file, metodo);
      haptic(r.estado === 'Conciliado' ? 'success' : 'warning');
      setFile(null);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div class="deposito-panel">
        <div class="loading"><span class="spinner" /></div>
      </div>
    );
  }
  if (!status) {
    return error ? <div class="deposito-panel"><div class="mireporte-error">⚠️ {error}</div></div> : null;
  }

  const est = status.estado || 'Pendiente';
  // Cliente interno (local propio de Sophisticated Management): validado al
  // cierre sin comprobante — no hubo depósito que "recibir".
  const esInterno = est === 'Conciliado' && status.metodo === 'Interno';
  // Cobro ya registrado en Ingresos (clientes en asociación): mismo par de
  // marcadores que Interno — Conciliado sin comprobante, nada que subir.
  const esIngreso = est === 'Conciliado' && status.metodo === 'Ingreso';
  const b = esInterno
    ? { ...BADGE.Conciliado, txt: '✅ Validado (local propio)' }
    : esIngreso
      ? { ...BADGE.Conciliado, txt: '💵 Ingreso registrado' }
      : (BADGE[est] || { txt: est, bg: 'var(--color-secondary)', fg: 'var(--color-hint)' });
  // No_Aplica does NOT hide the upload: the seeded state can be stale (factura
  // edited after closure) and we always want the client payment on record. The
  // engine re-derives the estado from the live factura on upload.
  const canUpload = est === 'Pendiente' || est === 'En_Revision' || est === 'No_Aplica';

  return (
    <div
      class="deposito-panel"
      style={{
        border: '1px solid var(--color-divider)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        marginTop: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Depósito del cliente</span>
        <span style={{ background: b.bg, color: b.fg, padding: '3px 8px', borderRadius: 'var(--radius-pill)', fontSize: 'var(--font-size-xs)', fontWeight: 600, whiteSpace: 'nowrap' }}>{b.txt}</span>
      </div>

      {est === 'Conciliado' && !esInterno && !esIngreso && <p class="mireporte-meta">El depósito fue validado. ¡Gracias! 🙌</p>}
      {esInterno && <p class="mireporte-meta">Local propio de Sophisticated Management — el depósito se valida solo al cerrar. No hace falta comprobante.</p>}
      {esIngreso && <p class="mireporte-meta">El cobro de este evento quedó registrado en Ingresos — no hace falta comprobante.</p>}
      {est === 'Mensual' && <p class="mireporte-meta">Este cliente factura mensual — Operaciones concilia el depósito del mes.</p>}
      {est === 'No_Aplica' && <p class="mireporte-meta">Este evento está marcado sin factura por cobrar. Si el cliente pagó, sube el comprobante igual — el estado se corrige solo.</p>}
      {est === 'En_Revision' && (
        <p class="mireporte-meta">
          Comprobante recibido. Operaciones lo está revisando
          {status.detectedAmount != null ? ` (detectado RD$ ${fmt(status.detectedAmount)})` : ''}.
        </p>
      )}

      {canUpload && (
        <>
          {typeof status.expected === 'number' && status.expected > 0 && (
            <p class="mireporte-meta">Monto a depositar: <b>RD$ {fmt(status.expected)}</b></p>
          )}
          <label class="mireporte-field" style={{ marginTop: 'var(--space-2)' }}>
            <span>¿Cómo se pagó?</span>
            <select value={metodo} onChange={(e) => setMetodo((e.target as HTMLSelectElement).value)}>
              <option value="Transferencia_Cliente">El cliente transfirió directo</option>
              <option value="Efectivo_Capitan">Recibí efectivo y lo deposité</option>
            </select>
          </label>
          <input
            type="file"
            accept="image/*,application/pdf"
            style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)', width: '100%' }}
            onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] || null)}
          />
          {error && <div class="mireporte-error" style={{ marginTop: 'var(--space-2)' }}>⚠️ {error}</div>}
          <button class="btn primary" disabled={busy || !file} onClick={onSubmit} style={{ marginTop: 'var(--space-2)' }}>
            {busy ? 'Subiendo…' : '📎 Subir comprobante'}
          </button>
        </>
      )}
      {!canUpload && error && <div class="mireporte-error">⚠️ {error}</div>}
    </div>
  );
}
